import React from 'react';
import { usePermissions } from '@/shared/hooks/usePermissions';

/**
 * PermissionGate - Conditionally renders children based on permissions
 * 
 * @param {string|string[]} permission - Single permission or array of permissions
 * @param {string} resource - Resource name (e.g., 'engines', 'resellers')
 * @param {string} action - Action name (e.g., 'create', 'edit', 'delete', 'view')
 * @param {boolean} requireAll - If true, requires all permissions; if false, requires any
 * @param {ReactNode} children - Content to render if permission check passes
 * @param {ReactNode} fallback - Content to render if permission check fails (optional)
 */
export function PermissionGate({
  permission,
  resource,
  action,
  requireAll = false,
  children,
  fallback = null,
}) {
  const { hasPermission, hasAnyPermission, hasAllPermissions, canPerformAction } = usePermissions();

  let hasAccess = false;

  // Check permission using resource + action
  if (resource && action) {
    hasAccess = canPerformAction(resource, action);
  }
  // Check single permission string
  else if (typeof permission === 'string') {
    hasAccess = hasPermission(permission);
  }
  // Check array of permissions
  else if (Array.isArray(permission)) {
    hasAccess = requireAll
      ? hasAllPermissions(permission)
      : hasAnyPermission(permission);
  }
  // Default: no permission check, show content
  else {
    hasAccess = true;
  }

  if (hasAccess) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
}

/**
 * Shorthand components for common permission checks
 */
export function CanView({ resource, children, fallback = null }) {
  return (
    <PermissionGate resource={resource} action="view" fallback={fallback}>
      {children}
    </PermissionGate>
  );
}

export function CanCreate({ resource, children, fallback = null }) {
  return (
    <PermissionGate resource={resource} action="create" fallback={fallback}>
      {children}
    </PermissionGate>
  );
}

export function CanEdit({ resource, children, fallback = null }) {
  return (
    <PermissionGate resource={resource} action="edit" fallback={fallback}>
      {children}
    </PermissionGate>
  );
}

export function CanDelete({ resource, children, fallback = null }) {
  return (
    <PermissionGate resource={resource} action="delete" fallback={fallback}>
      {children}
    </PermissionGate>
  );
}

export default PermissionGate;




